import React, { useState, useEffect, KeyboardEvent } from 'react';
import { Save, ArrowLeft, Plus, Upload, Trash2, ExternalLink } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { scheduleContentReferenceEmbeddingIndex } from '../../lib/contentReferenceEmbeddings';

interface Reference {
  id: string;
  name: string;
  content: string;
  usage: string;
  url?: string;
  notes?: string;
  useForEngine: boolean;
  date?: string;
  created_at: string;
  updated_at: string;
}

interface ReferenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  reference: Reference | null;
  onSuccess: () => void;
}

const ReferenceModal: React.FC<ReferenceModalProps> = ({
  isOpen,
  onClose,
  reference,
  onSuccess,
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<Partial<Reference>>({
    name: '',
    content: '',
    usage: 'email',
    url: '',
    notes: '',
    useForEngine: false,
    date: new Date().toISOString().split('T')[0],
  });
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isNewReference = !reference;

  useEffect(() => {
    if (reference) {
      setFormData({
        ...reference,
        date: reference.date ? new Date(reference.date).toISOString().split('T')[0] : undefined,
      });
    } else {
      setFormData({
        name: '',
        content: '',
        usage: 'email',
        url: '',
        notes: '',
        useForEngine: false,
        date: new Date().toISOString().split('T')[0],
      });
    }
  }, [reference, isOpen]);

  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        if (showDeleteConfirm) {
          setShowDeleteConfirm(false);
        } else {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleEscKey as any);
    return () => {
      document.removeEventListener('keydown', handleEscKey as any);
    };
  }, [isOpen, onClose, showDeleteConfirm]);

  const handleDelete = async () => {
    if (!reference) return;
    
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('content_references')
        .delete()
        .eq('id', reference.id);

      if (error) throw error;

      toast.success('Reference deleted successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(`Error deleting reference: ${error.message}`);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.name?.trim()) {
      toast.error('Name is required');
      return;
    }

    if (!formData.content?.trim()) {
      toast.error('Content is required');
      return;
    }

    setIsSaving(true);

    try {
      const dataToSubmit = {
        ...formData,
        name: formData.name.trim(),
        content: formData.content.trim(),
        url: formData.url?.trim() || null,
        notes: formData.notes?.trim() || null,
        date: formData.date ? new Date(formData.date).toISOString() : null,
        ...(isNewReference && user?.id ? { user_id: user.id } : {}),
      };

      let result;
      if (isNewReference) {
        result = await supabase.from('content_references').insert(dataToSubmit).select();
      } else {
        result = await supabase
          .from('content_references')
          .update(dataToSubmit)
          .eq('id', reference.id)
          .select();
      }

      if (result.error) throw result.error;

      const savedId = (result.data?.[0] as { id?: string } | undefined)?.id ?? reference?.id;
      if (savedId) {
        scheduleContentReferenceEmbeddingIndex(savedId);
      }

      toast.success(isNewReference ? 'Reference created successfully' : 'Reference updated successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenUrl = () => {
    if (formData.url) {
      window.open(formData.url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div 
      className={`fixed inset-y-0 right-0 w-full md:w-1/2 bg-white dark:bg-gray-800 shadow-xl transform transition-transform duration-300 ease-in-out ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      } z-50`}
    >
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <button
              onClick={onClose}
              className="mr-4 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-semibold">
              {isNewReference ? 'Create New Reference' : 'Edit Reference'}
            </h2>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring focus:ring-emerald-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 py-2.5 px-3"
                placeholder="Enter reference name..."
                required
              />
            </div>

            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Usage <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.usage}
                  onChange={(e) => setFormData({ ...formData, usage: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring focus:ring-emerald-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 py-2.5 px-3"
                  required
                >
                  <option value="linkedin">LinkedIn</option>
                  <option value="twitter">Twitter</option>
                  <option value="email">Email</option>
                </select>
              </div>

              <div className="flex items-center pt-6">
                <input
                  type="checkbox"
                  id="useForEngine"
                  checked={formData.useForEngine}
                  onChange={(e) => setFormData({ ...formData, useForEngine: e.target.checked })}
                  className="w-4 h-4 text-emerald-500 border-gray-300 rounded focus:ring-emerald-500"
                />
                <label htmlFor="useForEngine" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Use for content engine
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Date
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring focus:ring-emerald-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 py-2.5 px-3"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                URL
              </label>
              <div className="relative">
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring focus:ring-emerald-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 py-2.5 pl-3 pr-12"
                  placeholder="Enter source URL..."
                />
                {formData.url && (
                  <button
                    type="button"
                    onClick={handleOpenUrl}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-500 hover:text-emerald-500 transition-colors"
                    title="Open URL in new tab"
                  >
                    <ExternalLink className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Content <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={8}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring focus:ring-emerald-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 py-2.5 px-3"
                placeholder="Enter reference content..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring focus:ring-emerald-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 py-2.5 px-3"
                placeholder="Add any notes or comments..."
              />
            </div>

            {!isNewReference && !showDeleteConfirm && (
              <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 pt-4 mt-6">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center justify-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Reference
                </button>
              </div>
            )}

            {showDeleteConfirm && (
              <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 pt-4 mt-6">
                <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400 mb-4">
                    Are you sure you want to delete this reference? This action cannot be undone.
                  </p>
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(false)}
                      className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
                    >
                      {isDeleting ? 'Deleting...' : 'Yes, Delete'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </form>

        <div className="shrink-0 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSaving || showDeleteConfirm}
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg py-2.5 px-4 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                {isNewReference ? 'Create Reference' : 'Update Reference'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReferenceModal;